import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  FolderTree,
  GripVertical,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Tag,
} from 'lucide-react';

import { useMenuRootsQuery, useMenuTreeQuery } from '@/client/api/menus/menus.query';
import {
  useCreateMenuNodeMutation,
  useDeleteMenuNodeMutation,
  useMoveMenuNodeMutation,
  useReorderMenusMutation,
  useUpdateMenuNodeMutation,
} from '@/client/api/menus/menus.mutation';
import {
  FOOTER_MENU_ROOTS,
  FOOTER_MENU_ROOT_IDS,
  MenuRoot,
  MenuTreeNode,
} from '@/client/types/menus';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MenuNodeDialog,
  MenuNodeFormState,
  emptyForm,
  formToPayload,
  nodeToForm,
} from './MenuNodeDialog';

type FlatRow = {
  node: MenuTreeNode;
  depth: number;
  siblings: MenuTreeNode[];
  index: number;
};

const flattenTree = (
  nodes: MenuTreeNode[],
  collapsed: Set<number>,
  depth = 0,
): FlatRow[] => {
  const rows: FlatRow[] = [];
  nodes.forEach((node, index) => {
    rows.push({ node, depth, siblings: nodes, index });
    const kids = node.children ?? [];
    if (kids.length > 0 && !collapsed.has(node.menid)) {
      rows.push(...flattenTree(kids, collapsed, depth + 1));
    }
  });
  return rows;
};

const linkBadge = (node: MenuTreeNode) => {
  if (node.cmsoid != null) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
        <FileText className="h-3 w-3" />
        cms #{node.cmsoid}
      </span>
    );
  }
  if (node.ext_link) {
    return (
      <span className="inline-flex max-w-[180px] items-center gap-1 truncate rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
        <ExternalLink className="h-3 w-3 flex-shrink-0" />
        {node.ext_link}
      </span>
    );
  }
  if (node.etid != null) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
        <Tag className="h-3 w-3" />
        etid #{node.etid}
      </span>
    );
  }
  return (
    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
      label
    </span>
  );
};

const knownFooterLabel = (menid: number) =>
  FOOTER_MENU_ROOTS.find((r) => r.menid === menid)?.label;

const rootLabel = (root: MenuRoot) => {
  const known = knownFooterLabel(root.menid);
  const name = known ?? root.name ?? `Root #${root.menid}`;
  const count = root.node_count != null ? ` · ${root.node_count}` : '';
  return `${name}${count}`;
};

interface NavTreePanelProps {
  /** Focus on footer columns the public site loads (default). */
  mode?: 'footer' | 'all';
}

export const NavTreePanel = ({ mode = 'footer' }: NavTreePanelProps) => {
  const { data: roots = [], isLoading: rootsLoading } = useMenuRootsQuery();
  const [showAllRoots, setShowAllRoots] = useState(mode === 'all');
  const [selectedRoot, setSelectedRoot] = useState<number | null>(null);

  const displayRoots = useMemo(() => {
    if (showAllRoots) return roots;
    const footer = FOOTER_MENU_ROOTS.map((fr) => {
      const live = roots.find((r) => r.menid === fr.menid);
      return (
        live ??
        ({
          menid: fr.menid,
          name: fr.label,
          node_count: 0,
        } as MenuRoot)
      );
    });
    // Also include any API roots that look like footer but aren't in the known set
    const extras = roots.filter(
      (r) => FOOTER_MENU_ROOT_IDS.has(r.menid) && !FOOTER_MENU_ROOTS.some((f) => f.menid === r.menid),
    );
    return [...footer, ...extras];
  }, [roots, showAllRoots]);

  const defaultRoot =
    displayRoots.find((r) => r.menid === 1)?.menid ?? displayRoots[0]?.menid ?? null;
  const effectiveRoot = selectedRoot ?? defaultRoot;

  const { data: tree = [], isLoading: treeLoading } = useMenuTreeQuery({
    menid_root: effectiveRoot,
  });

  const createMutation = useCreateMenuNodeMutation();
  const updateMutation = useUpdateMenuNodeMutation();
  const deleteMutation = useDeleteMenuNodeMutation();
  const reorderMutation = useReorderMenusMutation();
  const moveMutation = useMoveMenuNodeMutation();

  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('New node');
  const [editingMenid, setEditingMenid] = useState<number | null>(null);
  const [formInitial, setFormInitial] = useState<MenuNodeFormState | undefined>();

  const rows = useMemo(() => flattenTree(tree, collapsed), [tree, collapsed]);

  const toggleCollapse = (menid: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(menid)) next.delete(menid);
      else next.add(menid);
      return next;
    });
  };

  const openCreate = (parent?: MenuTreeNode) => {
    setEditingMenid(null);
    setDialogTitle(parent ? `Add child under “${parent.name}”` : 'New footer link');
    setFormInitial(
      emptyForm({
        menid_root: String(parent?.menid_root ?? effectiveRoot ?? ''),
        menid_parent: String(parent?.menid ?? effectiveRoot ?? ''),
        order_by: '10',
      }),
    );
    setDialogOpen(true);
  };

  const openEdit = (node: MenuTreeNode) => {
    setEditingMenid(node.menid);
    setDialogTitle(`Edit “${node.name}”`);
    setFormInitial(nodeToForm(node));
    setDialogOpen(true);
  };

  const handleSave = async (form: MenuNodeFormState) => {
    const payload = formToPayload(form);
    if (!payload.name) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingMenid != null) {
        await updateMutation.mutateAsync({ menid: editingMenid, ...payload });
        toast.success('Link updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Link created');
      }
      setDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    }
  };

  const handleDelete = async (node: MenuTreeNode) => {
    if (!window.confirm(`Soft-delete “${node.name}” (#${node.menid})?`)) return;
    try {
      await deleteMutation.mutateAsync({ menid: node.menid });
      toast.success('Link deactivated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed');
    }
  };

  const swapOrder = async (siblings: MenuTreeNode[], index: number, dir: -1 | 1) => {
    const other = index + dir;
    if (other < 0 || other >= siblings.length) return;
    const a = siblings[index];
    const b = siblings[other];
    const orderA = a.order_by ?? index * 10;
    const orderB = b.order_by ?? other * 10;
    try {
      await reorderMutation.mutateAsync([
        { menid: a.menid, order_by: orderB },
        { menid: b.menid, order_by: orderA },
      ]);
      toast.success('Order updated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Reorder failed');
    }
  };

  const moveToParent = async (node: MenuTreeNode) => {
    const raw = window.prompt(
      `Move “${node.name}” to parent menid (current: ${node.menid_parent ?? 'none'}):`,
      String(node.menid_parent ?? node.menid_root ?? ''),
    );
    if (raw == null) return;
    const menid_parent = Number(raw);
    if (!Number.isFinite(menid_parent)) {
      toast.error('Invalid parent menid');
      return;
    }
    try {
      await moveMutation.mutateAsync({
        menid: node.menid,
        menid_parent,
        menid_root: node.menid_root ?? effectiveRoot ?? undefined,
      });
      toast.success('Node moved');
    } catch (e: any) {
      toast.error(e?.message ?? 'Move failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <FolderTree className="h-4 w-4 text-gray-500" />
          <h2 className="text-sm font-medium text-gray-800">Footer link columns</h2>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          These columns power the site footer (Company, Market, Legal…). This is{' '}
          <strong>not</strong> the Market · Homes · Locals header — edit that under Header
          categories. Each link can deep-link to a CMS page, URL, or category.
        </p>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-[220px] space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Footer column
            </label>
            {rootsLoading ? (
              <Skeleton className="h-9 w-64" />
            ) : (
              <select
                className="w-full min-w-[240px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                value={effectiveRoot ?? ''}
                onChange={(e) => setSelectedRoot(Number(e.target.value))}
              >
                {displayRoots.length === 0 && <option value="">No columns found</option>}
                {displayRoots.map((r) => (
                  <option key={r.menid} value={r.menid}>
                    #{r.menid} — {rootLabel(r)}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={showAllRoots}
                onChange={(e) => setShowAllRoots(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show all menu roots (advanced)
            </label>
            <Button
              className="gap-2"
              disabled={effectiveRoot == null}
              onClick={() => openCreate()}
            >
              <Plus className="h-4 w-4" />
              Add link
            </Button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b bg-gray-50 px-4 py-2.5">
          <FolderTree className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {knownFooterLabel(effectiveRoot ?? 0) ?? 'Column'} links
          </span>
          <span className="text-xs text-gray-400">
            Reorder · edit destination · soft-delete
          </span>
        </div>

        {treeLoading && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        )}

        {!treeLoading && rows.length === 0 && (
          <div className="px-6 py-16 text-center text-sm text-gray-500">
            No links in this column. Add a link to get started.
          </div>
        )}

        {!treeLoading &&
          rows.map(({ node, depth, siblings, index }) => {
            const kids = node.children ?? [];
            const hasChildren = kids.length > 0;
            const isCollapsed = collapsed.has(node.menid);
            const inactive = node.active === 0 || node.active === false;

            return (
              <div
                key={node.menid}
                className={`group flex items-center gap-2 border-b border-gray-50 px-3 py-2 hover:bg-gray-50/80 ${
                  inactive ? 'opacity-50' : ''
                }`}
                style={{ paddingLeft: 12 + depth * 20 }}
              >
                <GripVertical className="h-3.5 w-3.5 flex-shrink-0 text-gray-300" />

                <button
                  type="button"
                  onClick={() => hasChildren && toggleCollapse(node.menid)}
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-gray-400 ${
                    hasChildren ? 'hover:bg-gray-200 hover:text-gray-700' : 'invisible'
                  }`}
                  aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="truncate text-sm font-medium text-gray-900 hover:text-blue-700"
                      onClick={() => openEdit(node)}
                    >
                      {node.name}
                    </button>
                    <span className="font-mono text-[10px] text-gray-400">#{node.menid}</span>
                    {linkBadge(node)}
                    {inactive && (
                      <span className="text-[10px] font-medium uppercase text-amber-600">
                        inactive
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index === 0 || reorderMutation.isPending}
                    onClick={() => swapOrder(siblings, index, -1)}
                    title="Move up"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index >= siblings.length - 1 || reorderMutation.isPending}
                    onClick={() => swapOrder(siblings, index, 1)}
                    title="Move down"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => openCreate(node)}
                    title="Add child"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => openEdit(node)}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-1.5 text-xs text-gray-500"
                    onClick={() => moveToParent(node)}
                    title="Reparent"
                  >
                    Move
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-600"
                    onClick={() => handleDelete(node)}
                    title="Soft-delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
      </div>

      <MenuNodeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={dialogTitle}
        initial={formInitial}
        saving={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSave}
      />
    </div>
  );
};
